import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFinalOffer } from '@/lib/quotationService';
import type { CustomerFinalOfferData } from '@/lib/quotationTypes';
import type { QuotationDocumentData } from '@/quotation/document/types';
import { mapFinalOfferToDocument } from '@/quotation/document/mappers/mapFinalOfferToDocument';
import QuotationHtmlDocument from '@/quotation/html/QuotationHtmlDocument';
import { useQuotationPdfDownload } from '@/quotation/download/useQuotationPdfDownload';

export default function FinalOfferPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('quotation');
  const [offer, setOffer] = useState<CustomerFinalOfferData | null>(null);
  const [documentData, setDocumentData] = useState<QuotationDocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { downloading, failed, download } = useQuotationPdfDownload();

  const fetchOffer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Single API fetch → one immutable snapshot for both HTML and PDF.
      const data = await getFinalOffer(id);
      setOffer(data);
      setDocumentData(mapFinalOfferToDocument(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : '최종 견적 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOffer();
  }, [fetchOffer]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-500 rounded-full animate-spin" />
        <p className="text-sm text-ink-400">최종 견적 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (error || !offer || !documentData) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-rose-500 text-xl" />
        </div>
        <p className="text-sm text-rose-600 font-medium">{error || '최종 견적을 찾을 수 없습니다.'}</p>
        <button
          onClick={() => navigate('/my/quotes')}
          className="mt-3 px-4 py-2 text-xs font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          견적 목록으로
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* App shell header: back + identity + Download PDF */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/my/quotes')}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            견적 목록으로
          </button>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <h1 className="text-lg md:text-xl font-bold text-ink-700">{t('quotation.finalOffer')}</h1>
            <span className="text-xs font-mono text-ink-400">{offer.offerNumber}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              발행 완료
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {offer.preliminaryQuoteId && (
            <button
              onClick={() => navigate(`/my/quotes/${offer.preliminaryQuoteId}`)}
              className="px-4 py-2.5 text-xs font-bold text-warm-500 border border-warm-300 rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-arrow-left-line" />
              사전 견적 보기
            </button>
          )}
          <button
            onClick={() => download(documentData)}
            disabled={downloading}
            className="px-4 py-2.5 text-xs font-bold text-white bg-ink-700 rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
          >
            {downloading ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-download-2-line" />
            )}
            {downloading ? t('quotation.pdfGenerating') : t('quotation.pdfDownload')}
          </button>
        </div>
      </div>

      {/* PDF generation error */}
      {failed && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          <i className="ri-information-line mt-0.5 shrink-0" />
          <span>{t('quotation.pdfError')}</span>
        </div>
      )}

      {/* The actual HTML quotation — the customer-facing document */}
      <QuotationHtmlDocument data={documentData} />
    </div>
  );
}